import {
  ApplicationConfig,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  withInMemoryScrolling,
} from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { routes } from './app.routes';
import {
  BrowserModule,
  provideClientHydration,
} from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { provideToastr } from 'ngx-toastr';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { MomentModule } from 'ngx-moment';

import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeAr from '@angular/common/locales/ar';
import localeEn from '@angular/common/locales/en-AE';
import 'moment/locale/ar';
import 'moment/locale/en-au';
import { loadingInterceptor } from './@core/interceptors/loading.interceptor';
import { cacheInterceptor } from './@core/interceptors/cache.interceptor';
import { AuthModule, LogLevel } from 'angular-auth-oidc-client';
import moment from 'moment';
import { environment } from '../environments/environment';
import { NgxSpinnerModule } from 'ngx-spinner';

registerLocaleData(localeAr);
registerLocaleData(localeEn);

const ENGLISH = 'en';
const ARABIC = 'ar';
const DEFAULT_LANGUAGE = ARABIC;

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};
const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, inMemoryScrollingFeature),
    provideHttpClient(withFetch(), withInterceptors([cacheInterceptor, loadingInterceptor])),
    provideToastr({ positionClass: 'toast-top-center' }),
    provideAnimations(),
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient],
        },
      }),
      AuthModule.forRoot({
        config: {
          authority: environment.authPath,
          redirectUrl: `${environment.portalUrl}/signin-oidc`,
          postLogoutRedirectUri: environment.portalUrl,
          clientId: 'app1',
          scope: 'openid profile email offline_access',
          responseType: 'code',
          silentRenew: true,
          useRefreshToken: true,
          logLevel: LogLevel.Debug,
          maxIdTokenIatOffsetAllowedInSeconds: 600,
          disableIatOffsetValidation: true,
        },
      }),
      NgxSpinnerModule.forRoot({ type: 'ball-spin' }),
    ]),
    BrowserModule,
    BrowserAnimationsModule,
    MomentModule,
    { provide: LOCALE_ID, useFactory: getLocale },
  ],
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function getLocale(): string {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const userPreferredLocale = localStorage.getItem('language');
      if (userPreferredLocale) {
        updateDocumentAttributes(userPreferredLocale);
        return userPreferredLocale;
      }
    }
  } catch (error) {
    console.error('Error retrieving language from localStorage:', error);
  }
  return DEFAULT_LANGUAGE;
}

function updateDocumentAttributes(lang: string): void {
  const dir = lang === ENGLISH ? 'ltr' : 'rtl';
  document.documentElement.lang !== lang &&
    document.documentElement.setAttribute('lang', lang);
  document.documentElement.dir !== dir &&
    document.documentElement.setAttribute('dir', dir);
  moment.locale(lang);
}
